import {
  setupSinglePass,
  prepareSinglePassSource,
  drawSinglePass,
  cleanupSinglePass
} from "./index-8p04sssx.mjs";
import {
  WEBGPU_SINGLE_PASS_VERTEX,
  setupWebGpuSinglePass,
  cleanupWebGpuSinglePass,
  applyWebGpuSinglePass
} from "./index-tvfexb7s.mjs";

// src/rect-transform-runtime.ts
import { Internals } from "remotion";
var WEBGL_SHADER = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 fragColor;
uniform sampler2D uSource;
uniform vec2 uResolution;
uniform vec4 uRect;
void main() {
	vec2 p = vec2(gl_FragCoord.x, uResolution.y - gl_FragCoord.y);
	vec2 coverage = clamp(min(p + 0.5, uRect.xy + uRect.zw) - max(p - 0.5, uRect.xy), 0.0, 1.0);
	vec2 uv = (p - uRect.xy) / uRect.zw;
	if (any(lessThanEqual(uv, vec2(0.0))) || any(greaterThan(uv, vec2(1.0)))) { fragColor = vec4(0.0); return; }
	fragColor = texture(uSource, vec2(uv.x, 1.0 - uv.y)) * min(coverage.x, coverage.y);
}
`;
var WEBGPU_SHADER = `
${WEBGPU_SINGLE_PASS_VERTEX}
struct Params {
	resolution: vec2f,
	padding: vec2f,
	rect: vec4f,
}
@group(0) @binding(0) var source: texture_2d<f32>;
@group(0) @binding(1) var sourceSampler: sampler;
@group(0) @binding(2) var<uniform> params: Params;
@fragment fn fragment(input: VertexOutput) -> @location(0) vec4f {
	let p = input.position.xy;
	let coverage = clamp(min(p + vec2f(0.5), params.rect.xy + params.rect.zw) - max(p - vec2f(0.5), params.rect.xy), vec2f(0), vec2f(1));
	let uv = (p - params.rect.xy) / params.rect.zw;
	if (any(uv <= vec2f(0)) || any(uv > vec2f(1))) { return vec4f(0); }
	return textureSampleLevel(source, sourceSampler, uv, 0) * min(coverage.x, coverage.y);
}
`;
var createRectTransformImplementations = (label, getRect) => ({
  webgl2: Internals.createEffectImplementation({
    setup: (target) => setupSinglePass(target, WEBGL_SHADER, label, ["uResolution", "uRect"]),
    apply: ({ source, width, height, params, state, flipSourceY }) => {
      const rect = getRect(params, width, height);
      prepareSinglePassSource({
        state,
        source,
        width,
        height,
        flipSourceY
      });
      state.gl.uniform2f(state.uniforms.uResolution, width, height);
      state.gl.uniform4f(state.uniforms.uRect, rect.x, rect.y, rect.width, rect.height);
      drawSinglePass(state);
    },
    cleanup: cleanupSinglePass
  }),
  webgpu: Internals.createEffectImplementation({
    setup: (target) => setupWebGpuSinglePass(target, label),
    apply: ({ source, width, height, params, state, gpuDevice }) => {
      if (!gpuDevice) {
        throw new Error(`A WebGPU device is required for the ${label} effect`);
      }
      const rect = getRect(params, width, height);
      applyWebGpuSinglePass({
        state,
        source,
        width,
        height,
        device: gpuDevice,
        shader: WEBGPU_SHADER,
        uniformData: new Float32Array([
          width,
          height,
          0,
          0,
          rect.x,
          rect.y,
          rect.width,
          rect.height
        ])
      });
    },
    cleanup: cleanupWebGpuSinglePass
  })
});

export { createRectTransformImplementations };
